/**
 * Map `?dest=` selection → Season Summary destination key (Ask #51/#55).
 * Pure JS for `node --test`.
 */
import { countryKey } from './countryFilters.js';

/** @typedef {{ mode: 'all'|'some'|'none', keys: Set<string> }} DestSel */

/**
 * Path-safe pack key (mint/GCS cannot use spaces).
 * @param {string} raw
 */
export function seasonDestPackKey(raw) {
  const k = countryKey(raw);
  if (!k) return '';
  // Packs / mint use USA (not UNITED_STATES) and UNITED_KINGDOM.
  if (k === 'USA' || k === 'UNITED STATES' || k === 'UNITED_STATES') return 'USA';
  if (k === 'UNITED KINGDOM' || k === 'UNITED_KINGDOM' || k === 'UK') {
    return 'UNITED_KINGDOM';
  }
  return k.replace(/\s+/g, '_');
}

/**
 * @param {DestSel|null|undefined} destSel
 * @returns {{ key: string, fetchable: boolean, codes: string[], multi?: boolean }}
 *   key: ALL | NONE | single country code (e.g. USA) | sorted `--` join (CANADA--USA)
 */
export function resolveSeasonDestKey(destSel) {
  if (!destSel || destSel.mode === 'all') {
    // Ask #55: All maps to packs.ALL (world aggregate). No USA soft-default.
    return { key: 'ALL', fetchable: true, codes: [] };
  }
  if (destSel.mode === 'none') {
    return { key: 'NONE', fetchable: false, codes: [] };
  }
  const mapped = [...(destSel.keys || [])]
    .map((k) => seasonDestPackKey(k))
    .filter(Boolean);
  const codes = [...new Set(mapped)].sort();
  if (codes.length === 0) {
    return { key: 'NONE', fetchable: false, codes: [] };
  }
  if (codes.length === 1) {
    return { key: codes[0], fetchable: true, codes };
  }
  return { key: codes.join('--'), fetchable: true, codes, multi: true };
}

/**
 * Tables/cards only when the published pack destination (the DESTS part of
 * the scope) matches the filter key.
 * @param {string} filterKey
 * @param {string|null|undefined} packDestination
 */
export function seasonPackMatchesFilter(filterKey, packDestination) {
  const want = String(filterKey || '').trim().toUpperCase();
  const got = String(packDestination || '').trim().toUpperCase();
  if (!want || want === 'NONE') return false;
  return Boolean(got) && got === want;
}
